import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiWorker } from '../../api/worker';
import { useCurrentUser } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { toast } from 'react-hot-toast';
import { GameLeaderboardScopeTabs } from './common/GameLeaderboardScopeTabs';
import { 
  Trophy, Volume2, VolumeX, RotateCcw, Play, Pause, 
  Maximize2, Minimize2, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, ShieldAlert, Award
} from 'lucide-react';

interface HighScore {
  playerName: string;
  score: number;
  level?: number;
  date?: string;
  metadata?: Record<string, any>;
}

/** Detect if we're on a mobile device (not just narrow viewport). */
function isMobileDevice(): boolean {
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

/**
 * Attempt to lock screen orientation to landscape.
 * Returns true if lock succeeded.
 */
async function tryLockLandscape(): Promise<boolean> {
  try {
    const so = screen.orientation as ScreenOrientation & { lock?: (o: string) => Promise<void> };
    if (so?.lock) {
      await so.lock('landscape');
      return true;
    }
  } catch {
    // lock() threw — browser blocked it or unsupported
  }
  return false;
}

/** Unlock orientation if previously locked. */
function tryUnlockOrientation() {
  try {
    const so = screen.orientation as ScreenOrientation & { unlock?: () => void };
    if (so?.unlock) so.unlock();
  } catch {
    // ignore
  }
}

type Direction = 'UP' | 'DOWN' | 'LEFT' | 'RIGHT' | 'NONE';

interface Position {
  x: number;
  y: number;
}

interface Ghost {
  id: string;
  name: string;
  role: string;
  color: string;
  x: number; // grid float position
  y: number;
  dir: Direction;
  targetX: number;
  targetY: number;
  isFrightened: boolean;
  isEaten: boolean;
  scatterCorner: Position;
  homePos: Position;
  speed: number;
}

// Classic 28x31 Maze Layout: 1=Wall, 0=Dot, 2=PowerPellet, 3=Empty/GhostHouse
const MAZE_GRID: number[][] = [
  [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1],
  [1,0,0,0,0,0,0,0,0,0,0,0,0,1,1,0,0,0,0,0,0,0,0,0,0,0,0,1],
  [1,0,1,1,1,1,0,1,1,1,1,1,0,1,1,0,1,1,1,1,1,0,1,1,1,1,0,1],
  [1,2,1,1,1,1,0,1,1,1,1,1,0,1,1,0,1,1,1,1,1,0,1,1,1,1,2,1],
  [1,0,1,1,1,1,0,1,1,1,1,1,0,1,1,0,1,1,1,1,1,0,1,1,1,1,0,1],
  [1,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,1],
  [1,0,1,1,1,1,0,1,1,0,1,1,1,1,1,1,1,1,0,1,1,0,1,1,1,1,0,1],
  [1,0,1,1,1,1,0,1,1,0,1,1,1,1,1,1,1,1,0,1,1,0,1,1,1,1,0,1],
  [1,0,0,0,0,0,0,1,1,0,0,0,0,1,1,0,0,0,0,1,1,0,0,0,0,0,0,1],
  [1,1,1,1,1,1,0,1,1,1,1,1,3,1,1,3,1,1,1,1,1,0,1,1,1,1,1,1],
  [3,3,3,3,3,1,0,1,1,1,1,1,3,1,1,3,1,1,1,1,1,0,1,3,3,3,3,3],
  [3,3,3,3,3,1,0,1,1,3,3,3,3,3,3,3,3,3,3,1,1,0,1,3,3,3,3,3],
  [3,3,3,3,3,1,0,1,1,3,1,1,1,3,3,1,1,1,3,1,1,0,1,3,3,3,3,3],
  [1,1,1,1,1,1,0,1,1,3,1,3,3,3,3,3,3,1,3,1,1,0,1,1,1,1,1,1],
  [3,3,3,3,3,3,0,3,3,3,1,3,3,3,3,3,3,1,3,3,3,0,3,3,3,3,3,3], // Tunnel row 14
  [1,1,1,1,1,1,0,1,1,3,1,3,3,3,3,3,3,1,3,1,1,0,1,1,1,1,1,1],
  [3,3,3,3,3,1,0,1,1,3,1,1,1,1,1,1,1,1,3,1,1,0,1,3,3,3,3,3],
  [3,3,3,3,3,1,0,1,1,3,3,3,3,3,3,3,3,3,3,1,1,0,1,3,3,3,3,3],
  [3,3,3,3,3,1,0,1,1,3,1,1,1,1,1,1,1,1,3,1,1,0,1,3,3,3,3,3],
  [1,1,1,1,1,1,0,1,1,0,1,1,1,1,1,1,1,1,0,1,1,0,1,1,1,1,1,1],
  [1,0,0,0,0,0,0,0,0,0,0,0,0,1,1,0,0,0,0,0,0,0,0,0,0,0,0,1],
  [1,0,1,1,1,1,0,1,1,1,1,1,0,1,1,0,1,1,1,1,1,0,1,1,1,1,0,1],
  [1,0,1,1,1,1,0,1,1,1,1,1,0,1,1,0,1,1,1,1,1,0,1,1,1,1,0,1],
  [1,2,0,0,1,1,0,0,0,0,0,0,0,3,3,0,0,0,0,0,0,0,1,1,0,0,2,1],
  [1,1,1,0,1,1,0,1,1,0,1,1,1,1,1,1,1,1,0,1,1,0,1,1,0,1,1,1],
  [1,1,1,0,1,1,0,1,1,0,1,1,1,1,1,1,1,1,0,1,1,0,1,1,0,1,1,1],
  [1,0,0,0,0,0,0,1,1,0,0,0,0,1,1,0,0,0,0,1,1,0,0,0,0,0,0,1],
  [1,0,1,1,1,1,1,1,1,1,1,1,0,1,1,0,1,1,1,1,1,1,1,1,1,1,0,1],
  [1,0,1,1,1,1,1,1,1,1,1,1,0,1,1,0,1,1,1,1,1,1,1,1,1,1,0,1],
  [1,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,1],
  [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]
];

const GRID_COLS = 28;
const GRID_ROWS = 31;

export function NodeManGame() {
  const queryClient = useQueryClient();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Game States
  const [gameState, setGameState] = useState<'START' | 'PLAYING' | 'PAUSED' | 'GAMEOVER' | 'VICTORY'>('START');
  const [score, setScore] = useState<number>(0);
  const [level, setLevel] = useState<number>(1);
  const [lives, setLives] = useState<number>(3);
  const [powerTimeLeft, setPowerTimeLeft] = useState<number>(0);
  const [ghostMultiplier, setGhostMultiplier] = useState<number>(200);
  const [isSoundOn, setIsSoundOn] = useState<boolean>(true);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [isFakeFullscreen, setIsFakeFullscreen] = useState<boolean>(false);
  const [forceRotate, setForceRotate] = useState<boolean>(false);
  const { isSignedIn, user } = useCurrentUser();
  const { node, isGlobal } = useTenant();
  const hasActiveNode = Boolean(node && !isGlobal && node.id !== 'platform-root');
  const nodeName = hasActiveNode ? (node?.name || 'Organization') : '';
  const [leaderboardScope, setLeaderboardScope] = useState<'world' | 'node'>('world');
  const [playerName, setPlayerName] = useState<string>(() => user?.username || user?.fullName || user?.firstName || '');
  const [submittedScore, setSubmittedScore] = useState<boolean>(false);

  // Fill playerName from the auth user once it's available (render-phase adjustment;
  // setState during render with a previous-value guard is the sanctioned pattern).
  const [playerNameUser, setPlayerNameUser] = useState(user);
  if (user && !playerName && user !== playerNameUser) {
    setPlayerNameUser(user);
    setPlayerName(user.username || user.fullName || user.firstName || '');
  }

  // Sound Engine Refs
  const audioCtxRef = useRef<AudioContext | null>(null);
  const bgmOscRef = useRef<OscillatorNode | null>(null);
  const bgmGainRef = useRef<GainNode | null>(null);

  // High Scores API
  const { data: highScoresData } = useQuery<any>({
    queryKey: ['node-man-high-scores', leaderboardScope, leaderboardScope === 'node' ? node?.id : 'world'],
    queryFn: async () => {
      try {
        const params: any = {};
        if (leaderboardScope === 'node' && hasActiveNode && node?.id) {
          params.nodeId = node.id;
        }
        const res = await apiWorker.get('/api/tools/node-man/high-scores', { params, silent: true });
        return res.data;
      } catch (err) {
        console.warn('Failed to fetch node-man high scores:', err);
        return [];
      }
    }
  });

  const highScores: HighScore[] = Array.isArray(highScoresData)
    ? highScoresData
    : Array.isArray(highScoresData?.content)
      ? highScoresData.content
      : Array.isArray(highScoresData?.data)
        ? highScoresData.data
        : [];

  const submitScoreMutation = useMutation({
    mutationFn: async (scoreData: { playerName: string; score: number; level: number }) => {
      return await apiWorker.post('/api/tools/node-man/high-scores', {
        playerName: scoreData.playerName,
        score: scoreData.score,
        game: 'node-man',
        nodeId: hasActiveNode ? node?.id : undefined,
        metadata: {
          levelReached: scoreData.level,
          nodesConnected: Math.max(0, 160 - dotsRemainingRef.current)
        }
      }, { silent: true });
    },
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['node-man-high-scores'] });
      setSubmittedScore(true);
      const isNewHigh = res?.data?.isNewHighScore;
      toast.success(isNewHigh ? '🏆 New High Score Recorded!' : 'Score recorded!');
    },
    onError: (err: any) => {
      if (err?.response?.status === 401) {
        toast.error('Sign in required to save score to global leaderboard.', { id: 'nm-401' });
      } else {
        toast.error('Failed to submit score.');
      }
    }
  });

  // Dynamic Maze State (modified when dots are eaten)
  const currentGridRef = useRef<number[][]>(MAZE_GRID.map(row => [...row]));
  const dotsRemainingRef = useRef<number>(0);

  // Entity Positions
  const playerRef = useRef<{
    x: number;
    y: number;
    dir: Direction;
    nextDir: Direction;
    mouthAngle: number;
    mouthSpeed: number;
    speed: number;
  }>({
    x: 13.5,
    y: 23,
    dir: 'LEFT',
    nextDir: 'LEFT',
    mouthAngle: 0.2,
    mouthSpeed: 0.04,
    speed: 0.12
  });

  const ghostsRef = useRef<Ghost[]>([
    {
      id: 'blinky',
      name: 'Red Tape',
      role: 'Aggressive Bureaucracy',
      color: '#FF0055',
      x: 13.5,
      y: 11,
      dir: 'LEFT',
      targetX: 13,
      targetY: 23,
      isFrightened: false,
      isEaten: false,
      scatterCorner: { x: 26, y: 1 },
      homePos: { x: 13.5, y: 11 },
      speed: 0.09
    },
    {
      id: 'pinky',
      name: 'IRS Audit',
      role: 'Predictive Targeting',
      color: '#FF77BC',
      x: 13.5,
      y: 14,
      dir: 'UP',
      targetX: 13,
      targetY: 19,
      isFrightened: false,
      isEaten: false,
      scatterCorner: { x: 1, y: 1 },
      homePos: { x: 13.5, y: 14 },
      speed: 0.085
    },
    {
      id: 'inky',
      name: 'Bylaw Trap',
      role: 'Flanker',
      color: '#00F0FF',
      x: 11.5,
      y: 14,
      dir: 'RIGHT',
      targetX: 1,
      targetY: 29,
      isFrightened: false,
      isEaten: false,
      scatterCorner: { x: 26, y: 29 },
      homePos: { x: 11.5, y: 14 },
      speed: 0.08
    },
    {
      id: 'clyde',
      name: 'Overdue Bill',
      role: 'Random Wanderer',
      color: '#FF9900',
      x: 15.5,
      y: 14,
      dir: 'LEFT',
      targetX: 1,
      targetY: 29,
      isFrightened: false,
      isEaten: false,
      scatterCorner: { x: 1, y: 29 },
      homePos: { x: 15.5, y: 14 },
      speed: 0.075
    }
  ]);

  const powerTimerRef = useRef<number | null>(null);

  // Initialize Audio Context & Retro Chiptune Synth
  const playAudioEffect = useCallback((type: 'dot' | 'power' | 'ghost' | 'death' | 'victory') => {
    if (!isSoundOn) return;
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') {
        ctx.resume();
      }

      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);

      const now = ctx.currentTime;

      if (type === 'dot') {
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(440, now);
        osc.frequency.exponentialRampToValueAtTime(880, now + 0.05);
        gain.gain.setValueAtTime(0.1, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.05);
        osc.start(now);
        osc.stop(now + 0.05);
      } else if (type === 'power') {
        osc.type = 'square';
        osc.frequency.setValueAtTime(220, now);
        osc.frequency.linearRampToValueAtTime(660, now + 0.2);
        gain.gain.setValueAtTime(0.2, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.2);
        osc.start(now);
        osc.stop(now + 0.2);
      } else if (type === 'ghost') {
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(523.25, now);
        osc.frequency.setValueAtTime(659.25, now + 0.08);
        osc.frequency.setValueAtTime(783.99, now + 0.16);
        gain.gain.setValueAtTime(0.25, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.24);
        osc.start(now);
        osc.stop(now + 0.24);
      } else if (type === 'death') {
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(400, now);
        osc.frequency.exponentialRampToValueAtTime(50, now + 0.6);
        gain.gain.setValueAtTime(0.3, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.6);
        osc.start(now);
        osc.stop(now + 0.6);
      } else if (type === 'victory') {
        osc.type = 'square';
        osc.frequency.setValueAtTime(523.25, now);
        osc.frequency.setValueAtTime(659.25, now + 0.1);
        osc.frequency.setValueAtTime(783.99, now + 0.2);
        osc.frequency.setValueAtTime(1046.50, now + 0.3);
        gain.gain.setValueAtTime(0.2, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.4);
        osc.start(now);
        osc.stop(now + 0.4);
      }
    } catch (e) {
      console.warn('Audio play failed:', e);
    }
  }, [isSoundOn]);

  // Init Level & Maze
  const initLevel = useCallback((lvl: number) => {
    // Deep clone maze grid
    const gridCopy = MAZE_GRID.map(row => [...row]);
    let count = 0;
    for (let r = 0; r < GRID_ROWS; r++) {
      for (let c = 0; c < GRID_COLS; c++) {
        if (gridCopy[r][c] === 0 || gridCopy[r][c] === 2) {
          count++;
        }
      }
    }
    currentGridRef.current = gridCopy;
    dotsRemainingRef.current = count;

    // Reset player position
    playerRef.current.x = 14;
    playerRef.current.y = 23;
    playerRef.current.dir = 'LEFT';
    playerRef.current.nextDir = 'LEFT';
    playerRef.current.speed = 0.12 + Math.min(0.04, (lvl - 1) * 0.01);

    // Reset ghosts
    ghostsRef.current = [
      {
        id: 'blinky',
        name: 'Red Tape',
        role: 'Aggressive Bureaucracy',
        color: '#FF0055',
        x: 13.5,
        y: 11,
        dir: 'LEFT',
        targetX: 13,
        targetY: 23,
        isFrightened: false,
        isEaten: false,
        scatterCorner: { x: 26, y: 1 },
        homePos: { x: 13.5, y: 11 },
        speed: 0.085 + Math.min(0.03, (lvl - 1) * 0.008)
      },
      {
        id: 'pinky',
        name: 'IRS Audit',
        role: 'Predictive Targeting',
        color: '#FF77BC',
        x: 13.5,
        y: 14,
        dir: 'UP',
        targetX: 13,
        targetY: 19,
        isFrightened: false,
        isEaten: false,
        scatterCorner: { x: 1, y: 1 },
        homePos: { x: 13.5, y: 14 },
        speed: 0.08 + Math.min(0.03, (lvl - 1) * 0.008)
      },
      {
        id: 'inky',
        name: 'Bylaw Trap',
        role: 'Flanker',
        color: '#00F0FF',
        x: 11.5,
        y: 14,
        dir: 'RIGHT',
        targetX: 1,
        targetY: 29,
        isFrightened: false,
        isEaten: false,
        scatterCorner: { x: 26, y: 29 },
        homePos: { x: 11.5, y: 14 },
        speed: 0.075 + Math.min(0.03, (lvl - 1) * 0.008)
      },
      {
        id: 'clyde',
        name: 'Overdue Bill',
        role: 'Random Wanderer',
        color: '#FF9900',
        x: 15.5,
        y: 14,
        dir: 'LEFT',
        targetX: 1,
        targetY: 29,
        isFrightened: false,
        isEaten: false,
        scatterCorner: { x: 1, y: 29 },
        homePos: { x: 15.5, y: 14 },
        speed: 0.07 + Math.min(0.03, (lvl - 1) * 0.008)
      }
    ];
  }, []);

  // Full Game Reset
  const startNewGame = useCallback(() => {
    setScore(0);
    setLevel(1);
    setLives(3);
    setGameState('PLAYING');
    setSubmittedScore(false);
    setPowerTimeLeft(0);
    initLevel(1);
  }, [initLevel]);

  // Check if position is a wall
  const isWall = useCallback((x: number, y: number): boolean => {
    const col = Math.floor(x);
    const row = Math.floor(y);
    if (row < 0 || row >= GRID_ROWS || col < 0 || col >= GRID_COLS) return false;
    return currentGridRef.current[row]?.[col] === 1;
  }, []);

  // Check if direction is valid from cell
  const canMoveInDir = useCallback((x: number, y: number, dir: Direction): boolean => {
    const col = Math.round(x);
    const row = Math.round(y);
    let nextCol = col;
    let nextRow = row;

    if (dir === 'UP') nextRow -= 1;
    if (dir === 'DOWN') nextRow += 1;
    if (dir === 'LEFT') nextCol -= 1;
    if (dir === 'RIGHT') nextCol += 1;

    // Warp tunnel check
    if (row === 14 && (nextCol < 0 || nextCol >= GRID_COLS)) return true;

    if (nextRow < 0 || nextRow >= GRID_ROWS || nextCol < 0 || nextCol >= GRID_COLS) return false;
    return currentGridRef.current[nextRow][nextCol] !== 1;
  }, []);

  // Handle Keyboard Inputs
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const k = e.key.toLowerCase();
      const c = e.code;

      let d: Direction = 'NONE';
      if (c === 'ArrowUp' || c === 'KeyW' || k === 'arrowup' || k === 'w') d = 'UP';
      if (c === 'ArrowDown' || c === 'KeyS' || k === 'arrowdown' || k === 's') d = 'DOWN';
      if (c === 'ArrowLeft' || c === 'KeyA' || k === 'arrowleft' || k === 'a') d = 'LEFT';
      if (c === 'ArrowRight' || c === 'KeyD' || k === 'arrowright' || k === 'd') d = 'RIGHT';

      if (d !== 'NONE') {
        playerRef.current.nextDir = d;
        e.preventDefault();
      } else if (c === 'Space' || k === ' ') {
        setGameState(prev => {
          if (prev === 'PLAYING') return 'PAUSED';
          if (prev === 'PAUSED') return 'PLAYING';
          if (prev === 'START' || prev === 'GAMEOVER' || prev === 'VICTORY') {
            startNewGame();
            return 'PLAYING';
          }
          return prev;
        });
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [startNewGame, canMoveInDir]);

  // Power Pellet Trigger
  const triggerPowerPellet = useCallback(() => {
    playAudioEffect('power');
    setPowerTimeLeft(8);
    setGhostMultiplier(200);

    ghostsRef.current.forEach(g => {
      if (!g.isEaten) {
        g.isFrightened = true;
      }
    });

    if (powerTimerRef.current) clearInterval(powerTimerRef.current);
    const startTime = Date.now();
    const durationMs = 8000;

    powerTimerRef.current = window.setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, Math.ceil((durationMs - elapsed) / 1000));
      setPowerTimeLeft(remaining);

      if (remaining <= 0) {
        if (powerTimerRef.current) clearInterval(powerTimerRef.current);
        ghostsRef.current.forEach(g => {
          g.isFrightened = false;
        });
      }
    }, 200);
  }, [playAudioEffect]);

  // Main Canvas Render & Game Loop
  useEffect(() => {
    if (gameState !== 'PLAYING') return;

    let animFrameId: number;

    const renderLoop = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const cellW = canvas.width / GRID_COLS;
      const cellH = canvas.height / GRID_ROWS;

      const player = playerRef.current;

      // 1. UPDATE PLAYER POSITION & DIRECTION
      const pSpeed = player.speed;

      // Immediate 180° turn check
      if (player.nextDir !== 'NONE' && player.dir !== 'NONE') {
        const isOpposite = 
          (player.dir === 'LEFT' && player.nextDir === 'RIGHT') ||
          (player.dir === 'RIGHT' && player.nextDir === 'LEFT') ||
          (player.dir === 'UP' && player.nextDir === 'DOWN') ||
          (player.dir === 'DOWN' && player.nextDir === 'UP');
        if (isOpposite) {
          player.dir = player.nextDir;
        }
      }

      // 90° Turn or starting from stop when near cell center
      const pCellX = Math.round(player.x);
      const pCellY = Math.round(player.y);

      let dx = 0;
      let dy = 0;
      if (player.dir === 'UP') dy = -pSpeed;
      if (player.dir === 'DOWN') dy = pSpeed;
      if (player.dir === 'LEFT') dx = -pSpeed;
      if (player.dir === 'RIGHT') dx = pSpeed;

      const nextX = player.x + dx;
      const nextY = player.y + dy;

      let pCrossed = false;
      if (player.dir === 'LEFT' && player.x > pCellX && nextX <= pCellX) pCrossed = true;
      if (player.dir === 'RIGHT' && player.x < pCellX && nextX >= pCellX) pCrossed = true;
      if (player.dir === 'UP' && player.y > pCellY && nextY <= pCellY) pCrossed = true;
      if (player.dir === 'DOWN' && player.y < pCellY && nextY >= pCellY) pCrossed = true;

      if (pCrossed || player.dir === 'NONE') {
        if (player.nextDir !== 'NONE' && canMoveInDir(pCellX, pCellY, player.nextDir)) {
          player.x = pCellX;
          player.y = pCellY;
          player.dir = player.nextDir;
        } else if (player.dir !== 'NONE' && !canMoveInDir(pCellX, pCellY, player.dir)) {
          player.x = pCellX;
          player.y = pCellY;
          player.dir = 'NONE';
        }
      }

      // Move Player
      if (player.dir !== 'NONE') {
        let dx = 0;
        let dy = 0;
        if (player.dir === 'UP') dy = -pSpeed;
        if (player.dir === 'DOWN') dy = pSpeed;
        if (player.dir === 'LEFT') dx = -pSpeed;
        if (player.dir === 'RIGHT') dx = pSpeed;

        player.x += dx;
        player.y += dy;

        // Warp tunnel check
        if (player.y > 13.5 && player.y < 14.5) {
          if (player.x < -0.5) player.x = GRID_COLS - 0.5;
          else if (player.x >= GRID_COLS - 0.5) player.x = -0.5;
        }
      }

      // 2. CHECK DOT / POWER PELLET CONSUMPTION
      const currentCol = Math.round(player.x);
      const currentRow = Math.round(player.y);

      if (currentRow >= 0 && currentRow < GRID_ROWS && currentCol >= 0 && currentCol < GRID_COLS) {
        const tileVal = currentGridRef.current[currentRow]?.[currentCol];
        if (tileVal === 0) {
          // Regular Dues Dot
          currentGridRef.current[currentRow][currentCol] = 3;
          dotsRemainingRef.current -= 1;
          setScore(s => s + 10);
          playAudioEffect('dot');
        } else if (tileVal === 2) {
          // Board Authorization Power Pellet
          currentGridRef.current[currentRow][currentCol] = 3;
          dotsRemainingRef.current -= 1;
          setScore(s => s + 50);
          triggerPowerPellet();
        }
      }

      // Check Level Clear Condition
      if (dotsRemainingRef.current <= 0) {
        playAudioEffect('victory');
        setLevel(lvl => {
          const nextLvl = lvl + 1;
          initLevel(nextLvl);
          return nextLvl;
        });
      }

      // 3. UPDATE GHOSTS
      ghostsRef.current.forEach(ghost => {
        let gSpeed = ghost.speed;
        if (ghost.isFrightened) gSpeed *= 0.55;
        if (ghost.isEaten) gSpeed *= 1.8;

        // Check if ghost is inside ghost house area (rows 12-15, cols 10-17)
        const isInsideHouse = ghost.y >= 11.5 && ghost.y <= 15.5 && ghost.x >= 10 && ghost.x <= 17;

        if (isInsideHouse && !ghost.isEaten) {
          // Ghost House Exit Movement: Move to center x=13.5, then move UP out of door!
          if (Math.abs(ghost.x - 13.5) > 0.1) {
            ghost.dir = ghost.x < 13.5 ? 'RIGHT' : 'LEFT';
          } else {
            ghost.x = 13.5;
            ghost.dir = 'UP';
          }

          // Apply House Exit Movement STEP
          if (ghost.dir === 'RIGHT') ghost.x += gSpeed;
          if (ghost.dir === 'LEFT') ghost.x -= gSpeed;
          if (ghost.dir === 'UP') ghost.y -= gSpeed;

          if (ghost.y <= 11.0) {
            ghost.y = 11.0;
            ghost.x = 13.5;
            ghost.dir = 'LEFT';
          }
        } else {
          // Normal AI Navigation Outside House
          if (ghost.isEaten) {
            ghost.targetX = 13.5;
            ghost.targetY = 11;
            if (Math.hypot(ghost.x - 13.5, ghost.y - 11) < 0.5) {
              ghost.isEaten = false;
              ghost.isFrightened = false;
              ghost.y = 14;
            }
          } else if (ghost.isFrightened) {
            ghost.targetX = Math.floor(Math.random() * GRID_COLS);
            ghost.targetY = Math.floor(Math.random() * GRID_ROWS);
          } else {
            if (ghost.id === 'blinky') {
              ghost.targetX = Math.round(player.x);
              ghost.targetY = Math.round(player.y);
            } else if (ghost.id === 'pinky') {
              let px = Math.round(player.x);
              let py = Math.round(player.y);
              if (player.dir === 'UP') py -= 4;
              if (player.dir === 'DOWN') py += 4;
              if (player.dir === 'LEFT') px -= 4;
              if (player.dir === 'RIGHT') px += 4;
              ghost.targetX = px;
              ghost.targetY = py;
            } else if (ghost.id === 'inky') {
              const px = Math.round(player.x);
              const py = Math.round(player.y);
              const blinky = ghostsRef.current.find(g => g.id === 'blinky');
              const bx = blinky ? blinky.x : 0;
              const by = blinky ? blinky.y : 0;
              ghost.targetX = px + (px - bx);
              ghost.targetY = py + (py - by);
            } else if (ghost.id === 'clyde') {
              const dist = Math.hypot(ghost.x - player.x, ghost.y - player.y);
              if (dist > 8) {
                ghost.targetX = Math.round(player.x);
                ghost.targetY = Math.round(player.y);
              } else {
                ghost.targetX = ghost.scatterCorner.x;
                ghost.targetY = ghost.scatterCorner.y;
              }
            }
          }

          // Direction Decision at Cell Center
          const gCellX = Math.round(ghost.x);
          const gCellY = Math.round(ghost.y);

          let gdx = 0;
          let gdy = 0;
          if (ghost.dir === 'UP') gdy = -gSpeed;
          if (ghost.dir === 'DOWN') gdy = gSpeed;
          if (ghost.dir === 'LEFT') gdx = -gSpeed;
          if (ghost.dir === 'RIGHT') gdx = gSpeed;

          const gNextX = ghost.x + gdx;
          const gNextY = ghost.y + gdy;

          let gCrossed = false;
          if (ghost.dir === 'LEFT' && ghost.x > gCellX && gNextX <= gCellX) gCrossed = true;
          if (ghost.dir === 'RIGHT' && ghost.x < gCellX && gNextX >= gCellX) gCrossed = true;
          if (ghost.dir === 'UP' && ghost.y > gCellY && gNextY <= gCellY) gCrossed = true;
          if (ghost.dir === 'DOWN' && ghost.y < gCellY && gNextY >= gCellY) gCrossed = true;

          if (gCrossed || ghost.dir === 'NONE') {
            ghost.x = gCellX;
            ghost.y = gCellY;

            const possibleDirs: Direction[] = ['UP', 'DOWN', 'LEFT', 'RIGHT'];
            const validDirs = possibleDirs.filter(d => {
              // Prevent 180° reverse turn unless dead end
              if (d === 'UP' && ghost.dir === 'DOWN') return false;
              if (d === 'DOWN' && ghost.dir === 'UP') return false;
              if (d === 'LEFT' && ghost.dir === 'RIGHT') return false;
              if (d === 'RIGHT' && ghost.dir === 'LEFT') return false;

              // Prevent non-eaten ghosts from re-entering ghost door at row 11!
              if (d === 'DOWN' && gCellY === 11 && (gCellX === 13 || gCellX === 14) && !ghost.isEaten) return false;

              return canMoveInDir(gCellX, gCellY, d);
            });

            const dirsToConsider = validDirs.length > 0 ? validDirs : possibleDirs.filter(d => canMoveInDir(gCellX, gCellY, d));

            if (dirsToConsider.length > 0) {
              let bestDir = dirsToConsider[0];
              let minDistance = Infinity;

              dirsToConsider.forEach(d => {
                let testX = gCellX;
                let testY = gCellY;
                if (d === 'UP') testY -= 1;
                if (d === 'DOWN') testY += 1;
                if (d === 'LEFT') testX -= 1;
                if (d === 'RIGHT') testX += 1;
                const dist = Math.hypot(testX - ghost.targetX, testY - ghost.targetY);
                if (dist < minDistance) {
                  minDistance = dist;
                  bestDir = d;
                }
              });

              ghost.dir = bestDir;
            }
          }

          // Apply Ghost Movement Step
          if (ghost.dir !== 'NONE') {
            let gdx = 0;
            let gdy = 0;
            if (ghost.dir === 'UP') gdy = -gSpeed;
            if (ghost.dir === 'DOWN') gdy = gSpeed;
            if (ghost.dir === 'LEFT') gdx = -gSpeed;
            if (ghost.dir === 'RIGHT') gdx = gSpeed;

            ghost.x += gdx;
            ghost.y += gdy;

            // Warp tunnel
            if (ghost.y > 13.5 && ghost.y < 14.5) {
              if (ghost.x < -0.5) ghost.x = GRID_COLS - 0.5;
              else if (ghost.x >= GRID_COLS - 0.5) ghost.x = -0.5;
            }
          }
        }

        // 4. CHECK COLLISION WITH PLAYER
        const distToPlayer = Math.hypot(ghost.x - player.x, ghost.y - player.y);
        if (distToPlayer < 0.7) {
          if (ghost.isFrightened && !ghost.isEaten) {
            // Eat ghost
            ghost.isEaten = true;
            ghost.isFrightened = false;
            playAudioEffect('ghost');
            setScore(s => s + ghostMultiplier);
            setGhostMultiplier(m => m * 2);
          } else if (!ghost.isEaten && !ghost.isFrightened) {
            // Player caught
            playAudioEffect('death');
            setLives(l => {
              const newLives = l - 1;
              if (newLives <= 0) {
                setGameState('GAMEOVER');
                try {
                  const customSaved = localStorage.getItem('node_man_scores');
                  const parsed = customSaved ? JSON.parse(customSaved) : [];
                  const list = Array.isArray(parsed) ? parsed : [];
                  const name = playerName.trim() || (isSignedIn ? (user?.username || user?.fullName || 'NodeMan') : 'Guest');
                  list.push({
                    playerName: name,
                    score: score,
                    level: level,
                    date: new Date().toISOString().split('T')[0]
                  });
                  list.sort((a: any, b: any) => b.score - a.score);
                  localStorage.setItem('node_man_scores', JSON.stringify(list.slice(0, 20)));
                } catch {
                  // Best-effort leaderboard save; ignore storage failures.
                }
              } else {
                // Reset positions
                playerRef.current.x = 14;
                playerRef.current.y = 23;
                playerRef.current.dir = 'LEFT';
                playerRef.current.nextDir = 'LEFT';
                ghostsRef.current.forEach((g) => {
                  g.x = g.homePos.x;
                  g.y = g.homePos.y;
                  g.isEaten = false;
                  g.isFrightened = false;
                  if (g.id === 'blinky') g.dir = 'LEFT';
                  else if (g.id === 'pinky') g.dir = 'UP';
                  else if (g.id === 'inky') g.dir = 'RIGHT';
                  else g.dir = 'LEFT';
                });
              }
              return newLives;
            });
          }
        }
      });

      // 5. DRAW CANVAS GRAPHICS
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw Maze Walls & Dots
      for (let r = 0; r < GRID_ROWS; r++) {
        for (let c = 0; c < GRID_COLS; c++) {
          const val = currentGridRef.current[r][c];
          const x = c * cellW;
          const y = r * cellH;

          if (val === 1) {
            // Bauhaus Wall Block
            ctx.fillStyle = '#0044FF';
            ctx.fillRect(x + 1, y + 1, cellW - 2, cellH - 2);
            ctx.strokeStyle = '#FFFFFF';
            ctx.lineWidth = 1;
            ctx.strokeRect(x + 1, y + 1, cellW - 2, cellH - 2);
          } else if (val === 0) {
            // Dues Dot
            ctx.fillStyle = '#FFD600';
            ctx.beginPath();
            ctx.arc(x + cellW / 2, y + cellH / 2, Math.max(2, cellW * 0.15), 0, Math.PI * 2);
            ctx.fill();
          } else if (val === 2) {
            // Board Authorization Power Pellet
            const pulse = 1 + Math.sin(Date.now() * 0.01) * 0.2;
            ctx.fillStyle = '#FFFFFF';
            ctx.beginPath();
            ctx.arc(x + cellW / 2, y + cellH / 2, Math.max(5, cellW * 0.38) * pulse, 0, Math.PI * 2);
            ctx.fill();
            ctx.strokeStyle = '#FFD600';
            ctx.lineWidth = 2;
            ctx.stroke();
          }
        }
      }

      // Draw NodeMan Player (Bauhaus Org Node Executive)
      const px = player.x * cellW + cellW / 2;
      const py = player.y * cellH + cellH / 2;
      const radius = Math.min(cellW, cellH) * 0.7;

      // Animate mouth opening
      player.mouthAngle += player.mouthSpeed;
      if (player.mouthAngle > 0.45 || player.mouthAngle < 0.05) {
        player.mouthSpeed = -player.mouthSpeed;
      }

      let rotation = 0;
      if (player.dir === 'RIGHT') rotation = 0;
      if (player.dir === 'DOWN') rotation = Math.PI / 2;
      if (player.dir === 'LEFT') rotation = Math.PI;
      if (player.dir === 'UP') rotation = (Math.PI * 3) / 2;

      ctx.save();
      ctx.translate(px, py);
      ctx.rotate(rotation);

      // Node Body: Gold Circle with Black Border & Chomp Wedge
      ctx.fillStyle = '#FFD600';
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;

      ctx.beginPath();
      ctx.arc(0, 0, radius, player.mouthAngle, Math.PI * 2 - player.mouthAngle);
      ctx.lineTo(0, 0);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();

      // Node Core: Inner Executive Tech Diamond & Eye
      if (player.mouthAngle < 0.35) {
        ctx.fillStyle = '#000000';
        ctx.beginPath();
        ctx.arc(-radius * 0.1, -radius * 0.4, radius * 0.2, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#FFFFFF';
        ctx.beginPath();
        ctx.arc(-radius * 0.05, -radius * 0.45, radius * 0.08, 0, Math.PI * 2);
        ctx.fill();
      }

      // Org Node Hierarchy Antenna / Ring
      ctx.strokeStyle = '#0044FF';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(0, 0, radius * 0.5, player.mouthAngle + 0.2, Math.PI * 2 - player.mouthAngle - 0.2);
      ctx.stroke();

      ctx.restore();

      // Draw Ghosts (Custom Corporate Threat Entities)
      ghostsRef.current.forEach(ghost => {
        const gx = ghost.x * cellW + cellW / 2;
        const gy = ghost.y * cellH + cellH / 2;
        const gRadius = Math.min(cellW, cellH) * 0.65;

        ctx.save();
        ctx.translate(gx, gy);

        if (ghost.isEaten) {
          // Scanner eyes returning to home base
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(-gRadius * 0.5, -4, 8, 8);
          ctx.fillRect(gRadius * 0.1, -4, 8, 8);
          ctx.fillStyle = '#0044FF';
          ctx.fillRect(-gRadius * 0.3, -2, 4, 4);
          ctx.fillRect(gRadius * 0.3, -2, 4, 4);
        } else if (ghost.isFrightened) {
          // Frightened Threat: Vulnerable Blue Audit Block with Checkmark
          ctx.fillStyle = '#0044FF';
          ctx.strokeStyle = '#FFFFFF';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.roundRect(-gRadius, -gRadius, gRadius * 2, gRadius * 2, 4);
          ctx.fill();
          ctx.stroke();

          // Vulnerable Face (Pulsing yellow eyes)
          ctx.fillStyle = '#FFD600';
          ctx.fillRect(-gRadius * 0.4, -gRadius * 0.3, 5, 5);
          ctx.fillRect(gRadius * 0.2, -gRadius * 0.3, 5, 5);

          // Wavy mouth
          ctx.strokeStyle = '#FFD600';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(-gRadius * 0.5, gRadius * 0.3);
          ctx.lineTo(-gRadius * 0.2, gRadius * 0.1);
          ctx.lineTo(0, gRadius * 0.3);
          ctx.lineTo(gRadius * 0.2, gRadius * 0.1);
          ctx.lineTo(gRadius * 0.5, gRadius * 0.3);
          ctx.stroke();
        } else {
          // Distinct Threat Visuals based on Ghost ID
          ctx.fillStyle = ghost.color;
          ctx.strokeStyle = '#FFFFFF';
          ctx.lineWidth = 1.5;

          if (ghost.id === 'blinky') {
            // Red Tape: Stamped Legal Document Block with Ribbon/Tape Accent
            ctx.beginPath();
            ctx.roundRect(-gRadius, -gRadius, gRadius * 2, gRadius * 2, 3);
            ctx.fill();
            ctx.stroke();

            // Diagonal Red Tape Striping
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(-gRadius + 2, -gRadius + 4, gRadius * 2 - 4, 3);
            ctx.fillRect(-gRadius + 2, gRadius - 7, gRadius * 2 - 4, 3);

            // Angry Threat Eyes
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(-gRadius * 0.6, -gRadius * 0.3, 7, 7);
            ctx.fillRect(gRadius * 0.1, -gRadius * 0.3, 7, 7);
            ctx.fillStyle = '#000000';
            ctx.fillRect(-gRadius * 0.4, -gRadius * 0.2, 4, 4);
            ctx.fillRect(gRadius * 0.3, -gRadius * 0.2, 4, 4);
          } else if (ghost.id === 'pinky') {
            // IRS Audit: Audit Briefcase / Scanner Lens
            ctx.beginPath();
            ctx.arc(0, 0, gRadius, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();

            // Scanner Eye / Lens
            ctx.fillStyle = '#FFFFFF';
            ctx.beginPath();
            ctx.arc(0, -2, gRadius * 0.45, 0, Math.PI * 2);
            ctx.fill();
            ctx.fillStyle = '#FF0055';
            ctx.beginPath();
            ctx.arc(0, -2, gRadius * 0.2, 0, Math.PI * 2);
            ctx.fill();
          } else if (ghost.id === 'inky') {
            // Bylaw Trap: Hexagonal Gavel Shield
            ctx.beginPath();
            for (let i = 0; i < 6; i++) {
              const angle = (i * Math.PI) / 3;
              const hx = Math.cos(angle) * gRadius;
              const hy = Math.sin(angle) * gRadius;
              if (i === 0) ctx.moveTo(hx, hy);
              else ctx.lineTo(hx, hy);
            }
            ctx.closePath();
            ctx.fill();
            ctx.stroke();

            // Flanking eyes
            ctx.fillStyle = '#000000';
            ctx.fillRect(-gRadius * 0.5, -3, 5, 5);
            ctx.fillRect(gRadius * 0.1, -3, 5, 5);
          } else {
            // Clyde (Overdue Bill): Invoice Badge with Dollar Icon
            ctx.beginPath();
            ctx.roundRect(-gRadius * 0.85, -gRadius, gRadius * 1.7, gRadius * 2, 5);
            ctx.fill();
            ctx.stroke();

            // Inner Invoice Lines & Exclamation
            ctx.fillStyle = '#000000';
            ctx.font = 'bold 10px monospace';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('$', 0, -2);
          }
        }

        ctx.restore();
      });

      animFrameId = requestAnimationFrame(renderLoop);
    };

    animFrameId = requestAnimationFrame(renderLoop);
    return () => cancelAnimationFrame(animFrameId);
  }, [gameState, canMoveInDir, isWall, triggerPowerPellet, playAudioEffect, initLevel]);

  // Submit High Score
  const handleScoreSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!playerName.trim()) return;
    submitScoreMutation.mutate({
      playerName: playerName.trim(),
      score,
      level
    });
  };

  // Fullscreen Handler
  const effectiveFullscreen = isFullscreen || isFakeFullscreen;

  const toggleFullscreen = useCallback(async () => {
    const el = containerRef.current;
    if (!el) return;

    // --- EXIT ---
    if (isFullscreen || isFakeFullscreen) {
      const doc = document as Document & { webkitExitFullscreen?: () => Promise<void>; webkitFullscreenElement?: Element };
      if (document.fullscreenElement) {
        await document.exitFullscreen().catch(() => {});
      } else if (doc.webkitFullscreenElement && doc.webkitExitFullscreen) {
        doc.webkitExitFullscreen();
      }
      setIsFakeFullscreen(false);
      setForceRotate(false);
      tryUnlockOrientation();
      setIsFullscreen(false);
      return;
    }

    // --- ENTER ---
    let nativeWorked = false;
    try {
      if (el.requestFullscreen) {
        await el.requestFullscreen();
        nativeWorked = true;
      }
    } catch { /* blocked */ }

    if (!nativeWorked) {
      try {
        const webkitEl = el as HTMLElement & { webkitRequestFullscreen?: () => Promise<void> };
        if (webkitEl.webkitRequestFullscreen) {
          await webkitEl.webkitRequestFullscreen();
          nativeWorked = true;
        }
      } catch { /* blocked */ }
    }

    const locked = await tryLockLandscape();

    if (!nativeWorked) {
      setIsFakeFullscreen(true);
      setIsFullscreen(true);

      if (!locked && window.innerHeight > window.innerWidth) {
        setForceRotate(true);
      }
    }
  }, [isFullscreen, isFakeFullscreen]);

  // Sync isFullscreen with native fullscreen events
  useEffect(() => {
    const onChange = () => {
      const doc = document as Document & { webkitFullscreenElement?: Element };
      const inFS = !!(document.fullscreenElement || doc.webkitFullscreenElement);
      setIsFullscreen(inFS || isFakeFullscreen);
      if (!inFS && !isFakeFullscreen) {
        setForceRotate(false);
        tryUnlockOrientation();
      }
    };
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, [isFakeFullscreen]);

  // When orientation changes while in fake fullscreen, update forceRotate
  useEffect(() => {
    if (!isFakeFullscreen) return;
    const check = () => {
      const portrait = window.innerHeight > window.innerWidth;
      setForceRotate(portrait);
    };
    window.addEventListener('resize', check);
    window.addEventListener('orientationchange', check);
    return () => {
      window.removeEventListener('resize', check);
      window.removeEventListener('orientationchange', check);
    };
  }, [isFakeFullscreen]);

  // Inject viewport meta tag for iOS to prevent pinch-zoom on the game
  useEffect(() => {
    let meta = document.querySelector('meta[name="viewport"]') as HTMLMetaElement | null;
    const original = meta?.content || '';
    if (effectiveFullscreen && isMobileDevice()) {
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = 'viewport';
        document.head.appendChild(meta);
      }
      meta.content = 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover';
    }
    return () => {
      if (meta && original) meta.content = original;
    };
  }, [effectiveFullscreen]);

  // Handle ESC key for exiting fullscreen gracefully
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && (isFullscreen || isFakeFullscreen)) {
        if (document.fullscreenElement) {
          document.exitFullscreen().catch(() => {});
        }
        setIsFakeFullscreen(false);
        setForceRotate(false);
        setIsFullscreen(false);
        tryUnlockOrientation();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isFullscreen, isFakeFullscreen]);

  return (
    <div 
      ref={containerRef}
      className={`w-full max-w-5xl mx-auto p-4 sm:p-6 bg-black text-white border-8 border-black dark:border-white shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#FFFFFF] ${effectiveFullscreen ? 'fixed inset-0 z-[9999] overflow-y-auto max-w-none shadow-none border-none' : ''}`}
      style={forceRotate ? {
        /* CSS rotation fallback: rotate the entire wrapper 90° when stuck portrait */
        transform: 'rotate(90deg)',
        transformOrigin: 'center center',
        width: '100vh',
        height: '100vw',
        position: 'fixed',
        top: '50%',
        left: '50%',
        marginTop: 'calc(-50vw)',
        marginLeft: 'calc(-50vh)',
        zIndex: 9999,
        padding: '16px'
      } : undefined}
    >
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b-4 border-white mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl sm:text-5xl font-black uppercase tracking-tighter text-[#FFD600]">
              NodeMan
            </h1>
            <span className="bg-[#0044FF] text-white font-mono text-xs px-2 py-1 uppercase tracking-widest font-bold border border-white">
              Compliance Dash
            </span>
          </div>
          <p className="text-slate-300 font-mono text-sm mt-1">
            Collect Dues Dots & Executive Authorizations to outsmart Bureaucracy Ghosts!
          </p>
        </div>

        {/* Action Controls */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsSoundOn(!isSoundOn)}
            className="p-3 border-2 border-white bg-black hover:bg-[#0044FF] transition-colors"
            title={isSoundOn ? 'Mute Audio' : 'Unmute Audio'}
          >
            {isSoundOn ? <Volume2 className="w-5 h-5 text-[#FFD600]" /> : <VolumeX className="w-5 h-5 text-red-500" />}
          </button>
          <button
            onClick={toggleFullscreen}
            className="p-3 border-2 border-white bg-black hover:bg-[#0044FF] transition-colors"
            title="Toggle Fullscreen"
          >
            {effectiveFullscreen ? <Minimize2 className="w-5 h-5 text-white" /> : <Maximize2 className="w-5 h-5 text-white" />}
          </button>
        </div>
      </div>

      {/* Main Grid: Game Canvas + Scoreboard */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Canvas Game Screen */}
        <div className="lg:col-span-2 flex flex-col items-center">
          {/* Stats Bar */}
          <div className="w-full flex items-center justify-between bg-neutral-900 border-4 border-white p-3 font-mono mb-4 text-sm sm:text-base">
            <div>
              <span className="text-slate-400">SCORE:</span>{' '}
              <span className="text-[#FFD600] font-black text-xl">{score}</span>
            </div>
            <div>
              <span className="text-slate-400">LEVEL:</span>{' '}
              <span className="text-[#00F0FF] font-black text-xl">{level}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-slate-400 mr-1">NODE LIVES:</span>
              {Array.from({ length: lives }).map((_, i) => (
                <div key={i} className="w-4 h-4 rounded-full bg-[#FFD600] border border-white" />
              ))}
            </div>
          </div>

          {/* Power Pellet Timer Bar */}
          {powerTimeLeft > 0 && (
            <div className="w-full bg-[#0044FF] text-white font-mono text-center text-xs py-1 px-2 border-2 border-white mb-2 animate-pulse font-bold uppercase tracking-wider">
              ⚡ BOARD AUTHORIZATION ACTIVE: {powerTimeLeft}s (Ghosts Vulnerable!)
            </div>
          )}

          {/* Game Canvas Box */}
          <div className="relative w-full aspect-[28/31] max-w-[448px] bg-black border-4 border-white overflow-hidden">
            <canvas
              ref={canvasRef}
              width={448}
              height={496}
              className="w-full h-full block"
            />

            {/* Overlays */}
            {gameState === 'START' && (
              <div className="absolute inset-0 bg-black/90 flex flex-col items-center justify-center p-6 text-center">
                <div className="w-16 h-16 rounded-full bg-[#FFD600] border-4 border-white flex items-center justify-center mb-4 animate-bounce">
                  <Award className="w-8 h-8 text-black" />
                </div>
                <h2 className="text-3xl font-black text-[#FFD600] uppercase tracking-wider mb-2">READY TO DASH?</h2>
                <p className="font-mono text-slate-300 text-xs sm:text-sm max-w-xs mb-6">
                  Use Arrow keys or WASD to navigate the org grid. Collect all Dues Dots while dodging Red Tape & IRS Audits!
                </p>
                <button
                  onClick={startNewGame}
                  className="px-8 py-4 bg-[#FFD600] text-black font-black uppercase tracking-wider text-lg border-4 border-white hover:bg-white hover:text-black transition-all shadow-[4px_4px_0px_#FFFFFF]"
                >
                  START GAME
                </button>
              </div>
            )}

            {gameState === 'PAUSED' && (
              <div className="absolute inset-0 bg-black/85 flex flex-col items-center justify-center p-6 text-center">
                <h2 className="text-4xl font-black text-white uppercase tracking-widest mb-4">PAUSED</h2>
                <button
                  onClick={() => setGameState('PLAYING')}
                  className="px-6 py-3 bg-[#0044FF] text-white font-bold uppercase tracking-wider border-2 border-white hover:bg-[#FFD600] hover:text-black transition-all"
                >
                  RESUME GAME
                </button>
              </div>
            )}

            {gameState === 'GAMEOVER' && (
              <div className="absolute inset-0 bg-black/90 flex flex-col items-center justify-center p-6 text-center overflow-y-auto">
                <ShieldAlert className="w-12 h-12 text-red-500 mb-2" />
                <h2 className="text-3xl font-black text-red-500 uppercase tracking-widest mb-1">AUDIT OVERTAKE!</h2>
                <p className="font-mono text-slate-300 text-sm mb-4">Final Compliance Score: <span className="text-[#FFD600] font-bold">{score}</span></p>

                {/* Score Submission Form */}
                {isSignedIn ? (
                  !submittedScore ? (
                    <form onSubmit={handleScoreSubmit} className="w-full max-w-xs space-y-3 mb-4">
                      <input
                        type="text"
                        placeholder="Enter Leaderboard Tag"
                        maxLength={16}
                        value={playerName}
                        onChange={e => setPlayerName(e.target.value)}
                        className="w-full px-3 py-2 bg-neutral-900 border-2 border-white text-white font-mono text-sm text-center focus:outline-none focus:border-[#FFD600]"
                      />
                      <button
                        type="submit"
                        disabled={!playerName.trim() || submitScoreMutation.isPending}
                        className="w-full py-2 bg-[#0044FF] hover:bg-[#FFD600] text-white hover:text-black font-bold uppercase tracking-wider border-2 border-white text-sm disabled:opacity-50 transition-colors"
                      >
                        {submitScoreMutation.isPending ? 'SAVING...' : 'SAVE HIGH SCORE'}
                      </button>
                    </form>
                  ) : (
                    <p className="text-emerald-400 font-mono text-xs mb-4">✓ High score registered on global leaderboard!</p>
                  )
                ) : (
                  <div className="w-full max-w-xs p-3.5 bg-neutral-900 border-2 border-white/60 text-center mb-4">
                    <p className="text-[11px] text-gray-300 font-mono mb-1">Score saved locally on this device.</p>
                    <p className="text-xs font-bold text-white mb-2">Sign in to save your score to the global leaderboard.</p>
                    <Link
                      to="/sign-in"
                      className="block w-full py-2 bg-[#FFD600] hover:bg-white text-black font-bold uppercase tracking-wider text-xs border-2 border-black transition-colors text-center"
                    >
                      Sign In to Save Score
                    </Link>
                  </div>
                )}

                <button
                  onClick={startNewGame}
                  className="px-6 py-3 bg-[#FFD600] text-black font-black uppercase tracking-wider text-sm border-2 border-white hover:bg-white transition-all shadow-[4px_4px_0px_#FFFFFF]"
                >
                  PLAY AGAIN
                </button>
              </div>
            )}
          </div>

          {/* On-screen Touch D-Pad for Mobile */}
          <div className="mt-4 sm:hidden flex flex-col items-center gap-1">
            <button
              onClick={() => { playerRef.current.nextDir = 'UP'; }}
              className="p-4 bg-neutral-800 border-2 border-white active:bg-[#0044FF]"
            >
              <ArrowUp className="w-6 h-6 text-white" />
            </button>
            <div className="flex gap-4">
              <button
                onClick={() => { playerRef.current.nextDir = 'LEFT'; }}
                className="p-4 bg-neutral-800 border-2 border-white active:bg-[#0044FF]"
              >
                <ArrowLeft className="w-6 h-6 text-white" />
              </button>
              <button
                onClick={() => { playerRef.current.nextDir = 'DOWN'; }}
                className="p-4 bg-neutral-800 border-2 border-white active:bg-[#0044FF]"
              >
                <ArrowDown className="w-6 h-6 text-white" />
              </button>
              <button
                onClick={() => { playerRef.current.nextDir = 'RIGHT'; }}
                className="p-4 bg-neutral-800 border-2 border-white active:bg-[#0044FF]"
              >
                <ArrowRight className="w-6 h-6 text-white" />
              </button>
            </div>
          </div>
        </div>

        {/* Right Column: Leaderboard & Game Legend */}
        <div className="flex flex-col gap-6">
          {/* Leaderboard Panel */}
          <div className="bg-neutral-900 border-4 border-white p-4">
            <div className="flex items-center gap-2 border-b-2 border-white pb-2 mb-3">
              <Trophy className="w-5 h-5 text-[#FFD600]" />
              <h3 className="font-black uppercase tracking-wider text-sm text-[#FFD600]">
                Compliance Leaderboard
              </h3>
            </div>

            {hasActiveNode && (
              <GameLeaderboardScopeTabs
                scope={leaderboardScope}
                onScopeChange={setLeaderboardScope}
                nodeName={nodeName}
              />
            )}

            <div className="space-y-2 font-mono text-xs">
              {(!highScores || highScores.length === 0) ? (
                <p className="text-slate-400">No high scores registered yet.</p>
              ) : (
                (Array.isArray(highScores) ? highScores : []).slice(0, 7).map((hs, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between p-2 bg-black border border-neutral-700"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-[#FFD600] w-4">{idx + 1}.</span>
                      <span className="truncate max-w-[110px] text-white">{hs.playerName}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-[#00F0FF] font-bold">{hs.score}</span>
                      <span className="text-slate-500 text-[10px] block">LVL {hs.level ?? hs.metadata?.levelReached ?? 1}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Legend / Guide */}
          <div className="bg-neutral-900 border-4 border-white p-4 font-mono text-xs space-y-3">
            <h3 className="font-black uppercase tracking-wider text-sm text-white border-b-2 border-white pb-2">
              Org Threat Legend
            </h3>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#FF0055]" />
                <div>
                  <span className="font-bold text-[#FF0055]">Red Tape (Blinky)</span>
                  <p className="text-slate-400 text-[11px]">Directly pursues your Node position.</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#FF77BC]" />
                <div>
                  <span className="font-bold text-[#FF77BC]">IRS Audit (Pinky)</span>
                  <p className="text-slate-400 text-[11px]">Ambush hunter targeting tiles ahead.</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#00F0FF]" />
                <div>
                  <span className="font-bold text-[#00F0FF]">Bylaw Trap (Inky)</span>
                  <p className="text-slate-400 text-[11px]">Tactical flanker working with Red Tape.</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#FF9900]" />
                <div>
                  <span className="font-bold text-[#FF9900]">Overdue Bill (Clyde)</span>
                  <p className="text-slate-400 text-[11px]">Unpredictable corner wanderer.</p>
                </div>
              </div>

              <div className="pt-2 border-t border-neutral-700">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-3 h-3 rounded-full bg-white border border-[#FFD600]" />
                  <span className="font-bold text-white">Board Authorization</span>
                </div>
                <p className="text-slate-400 text-[11px]">Grants 8s immunity to digest bureaucracy for 200+ bonus pts!</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
